import { Component, DebugNode, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { TooltipModule } from 'primeng/tooltip';
import { SpinnerLoaderComponent } from '../../../shared/components/spinner-loader/spinner-loader.component';
import { RoleChangeDialogComponent } from './role-change-dialog/role-change-dialog.component';
import { SearchBoxComponent } from '../../../shared/components/search-box/search-box.component';
import { AdminService } from '../../../Utils/_services/admin/admin/admin.service';
import { SettingService } from '../../../Utils/_services/admin/settings/settings.service';
import { IPagedRequest } from '../../../Utils/_models/request.model';
import { PlanModalComponent } from './plan-modal/plan-modal.component';
import { SettingsGridComponent } from './settings-grid/settings-grid.component';
import { IUser } from '../../../Utils/_models/users.model';
import { ConfirmService } from '../../../Utils/_services/confirmation-dialog.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePipe,
    TooltipModule,
    SpinnerLoaderComponent,
    RoleChangeDialogComponent,
    SearchBoxComponent,
    PlanModalComponent,
    SettingsGridComponent
  ],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.css'
})
export class SettingsComponent implements OnInit {
  constructor(
    private settingService: SettingService,
    private adminService: AdminService,
    private confirmService: ConfirmService,
    private toastr: ToastrService
  ) { }

  // Toggles
  forceUpgradeEnabled = signal<boolean>(false);
  maintenanceEnabled = signal<boolean>(false);
  earlyReleaseEnabled = signal<boolean>(false);
  betaReleaseEnabled = signal<boolean>(false);
  appAdvertisementsEnabled = signal<boolean>(false);
  systemOutageEnabled = signal<boolean>(false);
  pwaInstallerEnabled = signal<boolean>(false);
  suspendAiServicesEnabled = signal<boolean>(false);
  freezeRegistrationsEnabled = signal<boolean>(false);
  // Role table
  roleLoader = signal(false);
  adminUsers = signal<IUser[]>([]);
  roleDialogVisible = signal<boolean>(false);
  selectedUser = signal<IUser | null>(null);
  isSuperAdmin = signal<boolean>(true);

  // Selection
  allSelected = signal<boolean>(false);
  hasSelection = signal<boolean>(false);

  // Search & sort
  searchTerm = signal<string | null>(null);
  currentSortField = signal<string | null>('role');   // 'role' maps to 'rolePriority' ordering
  currentSortOrder = signal<'asc' | 'desc' | null>(null);

  // Pagination
  currentPage = signal(1);
  totalCount = signal(0);
  pageSizes: number[] = [5, 10, 20, 50];
  pageSize = signal(5);
  totalPages: number = 0;
  startIndex: number = 1;
  endIndex: number = 5;
  previewPopupUrl = signal<string | null>(null);
  activeTab: 'settings' | 'roles' = 'settings';
  loading = signal(false);
  ngOnInit(): void {
    const savedTab = localStorage.getItem('adminSettingsActiveTab') as 'settings' | 'roles';
    if (savedTab) this.activeTab = savedTab;
    if (this.activeTab === 'settings') {
      this.fetchAppControls();
    } else if (this.activeTab === 'roles' && this.isSuperAdmin()) {
      this.fetchUsers();
    }
  }

  setActiveTab(tab: 'settings' | 'roles'): void {
    this.activeTab = tab;
    localStorage.setItem('adminSettingsActiveTab', tab);
    if (tab === 'settings') {
      this.fetchAppControls();
    } else if (tab === 'roles' && this.isSuperAdmin()) {
      this.fetchUsers();
    }
  }

fetchAppControls(): void {
  this.loading.set(true);

  this.settingService.getAppControls()
    .pipe(finalize(() => this.loading.set(false)))
    .subscribe({
      next: (res) => {
        this.forceUpgradeEnabled.set(!!res.data.force_upgrade);
        this.maintenanceEnabled.set(!!res.data.maintenance_mode);
        this.earlyReleaseEnabled.set(!!res.data.early_release);
        this.betaReleaseEnabled.set(!!res.data.beta_release);
        this.appAdvertisementsEnabled.set(!!res.data.app_advertisements);
        this.systemOutageEnabled.set(!!res.data.system_outage);
        this.pwaInstallerEnabled.set(!!res.data.pwa_installer);
        this.suspendAiServicesEnabled.set(!!res.data.suspend_ai_services);
        this.freezeRegistrationsEnabled.set(!!res.data.freeze_registrations);
      },
      error: (err) => {
        this.toastr.error(
          err?.error?.Errors?.message || 'Failed to load app controls'
        );
      }
    });
}

  fetchUsers(): void {
    this.roleLoader.set(true);

    // Map frontend sort fields to backend field names
    const sortFieldMap: Record<string, string> = {
      firstName: 'firstName',
      email: 'email',
      role: 'rolePriority',
      lastActive: 'lastLogin'
    };
    const sortField = this.currentSortField();
    const mappedSort = sortField ? (sortFieldMap[sortField] ?? sortField) : null;

    const req: IPagedRequest = {
      page: this.currentPage(),
      limit: this.pageSize(),
      search: this.searchTerm(),
      sort: mappedSort,
      sortOrder: mappedSort ? (this.currentSortOrder() ?? 'asc') : null
    };

    this.adminService.getAllUsers(req)
      .pipe(finalize(() => this.roleLoader.set(false)))
      .subscribe({
        next: res => {
          this.adminUsers.set(res.data?.users ?? []);
          this.totalCount.set(res.data.totalCount);
          this.allSelected.set(false);
          this.hasSelection.set(false);
          this.updatePagination();
        },
        error: () => {
          this.adminUsers.set([]);
          this.totalCount.set(0);
        }
      });
  }

  // Search
  onSearch(value: string | null) {
    this.currentPage.set(1);
    this.searchTerm.set(value);
    this.fetchUsers();
  }

  // Sort
  onSort(field: string): void {
    if (this.currentSortField() === field) {
      if (this.currentSortOrder() === 'asc') {
        this.currentSortOrder.set('desc');
      } else if (this.currentSortOrder() === 'desc') {
        this.currentSortOrder.set(null);
        this.currentSortField.set(null);
      }
    } else {
      this.currentSortField.set(field);
      this.currentSortOrder.set('asc');
    }
    this.fetchUsers();
  }

  // Selection logic
  toggleSelectAll(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.allSelected.set(isChecked);
    this.adminUsers.update(users => users.map(u => ({ ...u, selected: isChecked })));
    this.updateSelectAllState();
  }

  toggleSelection(user: IUser, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.adminUsers.update(users => users.map(u => u._id === user._id ? { ...u, selected: isChecked } : u));
    this.updateSelectAllState();
  }

  updateSelectAllState(): void {
    const users = this.adminUsers();
    if (users.length === 0) {
      this.allSelected.set(false);
      this.hasSelection.set(false);
      return;
    }
    const selectedCount = users.filter(u => u.selected).length;
    this.allSelected.set(selectedCount === users.length);
    this.hasSelection.set(selectedCount > 0);
  }

  getSortIcon(field: string): string {
    const active = this.currentSortField();
    if (!active) {
      if (field === 'lastActive') return '↓';
      return '⇅';
    }

    if (active !== field) return '⇅';
    if (this.currentSortOrder() === 'asc') return '↑';
    if (this.currentSortOrder() === 'desc') return '↓';
    return '⇅';
  }

  // Pagination
  updatePagination() {
    this.totalPages = Math.ceil(this.totalCount() / this.pageSize());
    this.startIndex = this.totalCount() === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1;
    this.endIndex = Math.min(this.currentPage() * this.pageSize(), this.totalCount());
  }

  onPageSizeChange(newSize: number) {
    this.pageSize.set(newSize);
    this.currentPage.set(1);
    this.fetchUsers();
  }

  previousPage() {
    if (this.currentPage() > 1) {
      this.currentPage.update(v => v - 1);
      this.fetchUsers();
    }
  }

  nextPage() {
    if (this.currentPage() < this.totalPages) {
      this.currentPage.update(v => v + 1);
      this.fetchUsers();
    }
  }

  // Toggles
  private async toggleSetting(
    setting: WritableSignal<boolean>,
    options: {
      title: string;
      description: string;
    }
  ): Promise<void> {
    const newValue = !setting();

    const confirmed = await this.confirmService.confirm({
      header: `${newValue ? 'Enable' : 'Disable'} ${options.title}`,
      bodyHeader: `Are you sure you want to ${newValue ? 'enable' : 'disable'} ${options.title}?`,
      bodySubtitle: options.description,
      yesButtonText: newValue ? 'Enable' : 'Disable',
      noButtonText: 'Cancel',
      yesButtonColor: newValue ? '#3b82f6' : '#ef4444',
    });

    if (!confirmed) return;

    setting.set(newValue);

    this.settingService.updateAppControls({
      forceUpgrade: this.forceUpgradeEnabled(),
      maintenanceMode: this.maintenanceEnabled(),
      earlyRelease: this.earlyReleaseEnabled(),
      betaRelease: this.betaReleaseEnabled(),
      appAdvertisements: this.appAdvertisementsEnabled(),
      systemOutage: this.systemOutageEnabled(),
      pwaInstaller: this.pwaInstallerEnabled(),
      suspendAiServices: this.suspendAiServicesEnabled(),
      freezeRegistrations: this.freezeRegistrationsEnabled(),
    }).subscribe({
      next: (res) => {
        this.toastr.success(res.msg);
      },
      error: (err) => {
        setting.set(!newValue);

        this.toastr.error(
          err?.error?.Errors?.message || 'Failed to update app controls'
        );
      }
    });
  }

  async onForceUpgradeToggle() {
    await this.toggleSetting(this.forceUpgradeEnabled, {
      title: 'Force Upgrade',
      description: !this.forceUpgradeEnabled()
        ? 'All users will be required to download the newest version of the app.'
        : 'Users will no longer be forced to download the newest version.'
    });
  }

  async onMaintenanceToggle() {
    await this.toggleSetting(this.maintenanceEnabled, {
      title: 'Maintenance Mode',
      description: !this.maintenanceEnabled()
        ? 'A maintenance banner will be displayed to all users across the app.'
        : 'The maintenance banner will be removed and normal operations will resume.'
    });
  }

  async onEarlyReleaseToggle() {
    await this.toggleSetting(this.earlyReleaseEnabled, {
      title: 'Early Release',
      description: !this.earlyReleaseEnabled()
        ? 'Shows a banner on the landing page and login screen letting users know that the app is in "Early Release" and there may be bugs.'
        : 'Early release banner will be removed.'
    });
  }

  async onBetaReleaseToggle() {
    await this.toggleSetting(this.betaReleaseEnabled, {
      title: 'Beta Release',
      description: !this.betaReleaseEnabled()
        ? 'Early access to new features will be enabled for selected beta users.'
        : 'Beta access features will be disabled.'
    });
  }

  async onAppAdvertisementsToggle() {
    await this.toggleSetting(this.appAdvertisementsEnabled, {
      title: 'App Advertisements',
      description: !this.appAdvertisementsEnabled()
        ? 'Google Play link and Brand Images with category "Advertisment" will be displayed.'
        : 'Advertisements will be hidden.'
    });
  }

  async onSystemOutageToggle() {
    await this.toggleSetting(this.systemOutageEnabled, {
      title: 'System Outage / Force Global Logout',
      description: !this.systemOutageEnabled()
        ? 'This will immediately terminate all active user sessions. Admin sessions will be terminated after 30 minutes. Users cannot log back in until disabled.'
        : 'Normal operations will resume and users will be allowed to log back in.'
    });
  }

  async onPwaInstallerToggle() {
    await this.toggleSetting(this.pwaInstallerEnabled, {
      title: 'PWA Installer Prompt',
      description: !this.pwaInstallerEnabled()
        ? 'A prompt asking iOS/Safari users to install the PWA will be displayed.'
        : 'The PWA installer prompt will be hidden.'
    });
  }

  async onSuspendAiServicesToggle() {
    await this.toggleSetting(this.suspendAiServicesEnabled, {
      title: 'Suspend AI Services',
      description: !this.suspendAiServicesEnabled()
        ? 'AI matching and safety services will be suspended temporarily.'
        : 'AI services will resume normal operation.'
    });
  }

  async onFreezeRegistrationsToggle() {
    await this.toggleSetting(this.freezeRegistrationsEnabled, {
      title: 'Freeze Registrations',
      description: !this.freezeRegistrationsEnabled()
        ? 'New user registration signup flow will be blocked.'
        : 'New user registrations will be allowed to signup normally.'
    });
  }
  // Role dialog
  openRoleDialog(user: any) {
    this.selectedUser.set(user);
    this.roleDialogVisible.set(true);
  }

  closeRoleDialog() {
    this.roleDialogVisible.set(false);
    this.selectedUser.set(null);
  }

async onRoleChanged(event: { userId: string; newRole: string }) {
  const user = this.adminUsers().find(u => u._id === event.userId);

  if (!user) return;

  const confirmed = await this.confirmService.confirm({
    header: '',
    bodyHeader: 'Update User Role',
    bodySubtitle: `Are you sure you want to change ${user.firstName} ${user.lastName}'s role to "${event.newRole}"?`,
    yesButtonText: 'Update',
    noButtonText: 'Cancel',
    yesBtnCss: 'btn-primary',
    noBtnCss: 'btn-secondary',
    width: '420px'
  });

  if (!confirmed) return;

  this.loading.set(true);

  this.adminService
    .updateUserRole(
      [event.userId],
      event.newRole as 'user' | 'admin' | 'super_admin'
    )
    .pipe(finalize(() => this.loading.set(false)))
    .subscribe({
      next: (res) => {
        this.toastr.success(
          res?.data?.message || 'User role updated successfully'
        );

        this.adminUsers.update(users =>
          users.map(u =>
            u._id === event.userId
              ? { ...u, role: event.newRole }
              : u
          )
        );
      },
      error: (err) => {
        this.toastr.error(
          err?.error?.Errors?.message || 'Failed to update user role'
        );
      }
    });
}

async onBulkRoleChange() {
  const selectedUsers = this.adminUsers().filter(u => u.selected);
  if (selectedUsers.length === 0) return;

  const confirmed = await this.confirmService.confirm({
    header: '',
    bodyHeader: 'Update Multiple Users',
    bodySubtitle: `Are you sure you want to change the role of ${selectedUsers.length} selected user(s) to "User"?`,
    yesButtonText: 'Update',
    noButtonText: 'Cancel',
    yesBtnCss: 'btn-primary',
    noBtnCss: 'btn-secondary',
    width: '420px'
  });

  if (!confirmed) return;

  this.loading.set(true);
  
  const userIds = selectedUsers.map(u => u._id!);

  this.adminService.updateUserRole(userIds, 'user')
    .pipe(finalize(() => this.loading.set(false)))
    .subscribe({
      next: () => {
        this.toastr.success('Users roles updated successfully');
        this.fetchUsers(); // Refresh the list
      },
      error: (err) => {
        this.toastr.error(err?.error?.Errors?.message || 'Failed to update some user roles');
        this.fetchUsers(); // Refresh to get consistent state
      }
    });
}


  getRoleBadgeClass(role: string): string {
    return `badge-${(role || '').toLowerCase()}`;
  }

  getProfileImage(u: IUser): string | undefined {
    const photos = (u as any).profilePhotos as any[];
    if (!photos || photos.length === 0) return undefined;
    const primary = photos.find(p => p.isPrimary);
    return (primary ?? photos[0])?.signedUrl || undefined;
  }

  getDisplayName(u: IUser): string {
    const first = u.firstName?.trim();
    const last = u.lastName?.trim();
    if (!first && !last) return '';
    return `${first ?? ''} ${last ?? ''}`.trim();
  }

  getInitials(u: IUser): string {
    const first = u.firstName?.trim()?.[0] ?? '';
    const last = u.lastName?.trim()?.[0] ?? '';
    const initials = (first + last).toUpperCase();
    return initials || 'N/A';
  }

  openPreview(url: string): void {
    this.previewPopupUrl.set(url);
  }

  closePreview(): void {
    this.previewPopupUrl.set(null);
  }
}